import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { useEnterpriseOrg } from '../context/EnterpriseOrgContext';
import { type EnterpriseWorkspaceTimelineRow } from '../lib/enterprise-api';
import { listEnterpriseWorkspaceCheckIns, type EnterpriseWorkspaceCheckIn } from '../lib/enterprise-workspace-local';
import { loadWorkspaceTimeline } from '../lib/enterprise-work-engine';

export type UnifiedEnterpriseCheckIn = {
  id: string;
  organizationId: string | null;
  relationshipDomain: 'romantic' | 'friendship' | 'family' | 'leadership';
  rtiScore: number;
  emotionalAverage: number | null;
  triggers: string[];
  entityLabel: string | null;
  note: string | null;
  occurredAt: string;
};

function mapServerRow(row: EnterpriseWorkspaceTimelineRow): UnifiedEnterpriseCheckIn {
  const metadata = (row.metadata || {}) as Record<string, unknown>;
  return {
    id: row.id,
    organizationId: row.organization_id,
    relationshipDomain: row.relationship_domain,
    rtiScore: row.rti_score,
    emotionalAverage: row.emotional_average ?? null,
    triggers: Array.isArray(row.triggers) ? row.triggers : [],
    entityLabel: typeof metadata.entityLabel === 'string' ? metadata.entityLabel : null,
    note: typeof metadata.note === 'string' ? metadata.note : null,
    occurredAt: row.occurred_at,
  };
}

function mapLocalRow(row: EnterpriseWorkspaceCheckIn): UnifiedEnterpriseCheckIn {
  return {
    id: row.id,
    organizationId: row.organizationId,
    relationshipDomain: row.relationshipDomain,
    rtiScore: row.rtiScore,
    emotionalAverage: row.emotionalAverage,
    triggers: row.triggers,
    entityLabel: row.entityLabel,
    note: row.note,
    occurredAt: row.occurredAt,
  };
}

export function useEnterpriseWorkspaceCheckIns() {
  const { accessToken } = useAuth();
  const { activeOrgId, hasActiveOrgAccess } = useEnterpriseOrg();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<'organization' | 'self' | null>(null);
  const [serverRows, setServerRows] = useState<UnifiedEnterpriseCheckIn[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!accessToken || !activeOrgId || !hasActiveOrgAccess) {
      setServerRows([]);
      setError(null);
      setLoading(false);
      setScope(null);
      return;
    }
    setLoading(true);
    setError(null);
    void loadWorkspaceTimeline(accessToken, activeOrgId, 180)
      .then((res) => {
        if (cancelled) return;
        if (!res.success || !res.timeline) {
          setServerRows([]);
          setError(res.error || 'Failed to load enterprise workspace check-ins');
          setScope(null);
          return;
        }
        setServerRows(res.timeline.map(mapServerRow));
        setScope(res.scope || null);
      })
      .catch((e) => {
        if (cancelled) return;
        setServerRows([]);
        setError(e instanceof Error ? e.message : String(e));
        setScope(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, activeOrgId, hasActiveOrgAccess]);

  const rows = useMemo(() => {
    const localRows = activeOrgId ? listEnterpriseWorkspaceCheckIns(activeOrgId).map(mapLocalRow) : [];
    const dedup = new Map<string, UnifiedEnterpriseCheckIn>();
    for (const row of [...serverRows, ...localRows]) {
      if (!dedup.has(row.id)) dedup.set(row.id, row);
    }
    return Array.from(dedup.values()).sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
  }, [activeOrgId, serverRows]);

  return { rows, loading, error, scope };
}

